import { View, Text, Pressable, Platform } from "react-native";
import { Link } from "expo-router";
import { Ionicons } from "@expo/vector-icons";

/**
 * FAQ item component
 */
interface FAQItemProps {
  question: string;
  answer: string;
}

const FAQItem = ({ question, answer }: FAQItemProps) => (
  <View className="mb-6 p-5 bg-gray-50 rounded-xl">
    <Text className="text-lg font-semibold text-gray-900 mb-2">{question}</Text>
    <Text className="text-gray-600 leading-7">{answer}</Text>
  </View>
);

/**
 * Contact card component
 */
interface ContactCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  description: string;
  value: string;
  color: string;
}

const ContactCard = ({ icon, title, description, value, color }: ContactCardProps) => (
  <View className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
    <View className="flex-row items-start gap-4">
      <View
        className="w-12 h-12 rounded-xl items-center justify-center"
        style={{ backgroundColor: color + "15" }}
      >
        <Ionicons name={icon} size={24} color={color} />
      </View>
      <View className="flex-1">
        <Text className="text-lg font-semibold text-gray-900">{title}</Text>
        <Text className="text-sm text-gray-500 mt-1">{description}</Text>
        <Text className="text-base font-medium mt-2" style={{ color }}>
          {value}
        </Text>
      </View>
    </View>
  </View>
);

/**
 * Support page for the Staatsen Familie App
 */
export default function SupportPage() {
  // Only render on web platform
  if (Platform.OS !== "web") {
    return null;
  }

  return (
    <View className="flex-1 min-h-screen">
      {/* Header */}
      <View className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-12">
        <View className="max-w-2xl mx-auto">
          <Link href="../(web)" asChild>
            <Pressable
              className="flex-row items-center gap-2 mb-6"
              style={{ cursor: "pointer" } as any}
            >
              <Ionicons name="arrow-back" size={20} color="white" />
              <Text className="text-white font-medium">Terug</Text>
            </Pressable>
          </Link>

          <View className="flex-row items-center gap-4">
            <View className="w-14 h-14 bg-white/20 rounded-2xl items-center justify-center">
              <Ionicons name="help-circle" size={28} color="white" />
            </View>
            <View>
              <Text className="text-2xl md:text-3xl font-bold text-white">
                Support & Contact
              </Text>
              <Text className="text-emerald-200 mt-1">
                We helpen je graag verder
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Content */}
      <View className="flex-1 px-6 py-12">
        <View className="max-w-2xl mx-auto w-full">
          {/* Contact Options */}
          <Text className="text-2xl font-bold text-gray-900 mb-6">
            Neem Contact Op
          </Text>

          <View className="gap-4 mb-12">
            <ContactCard
              icon="mail"
              title="E-mail Support"
              description="Stuur ons een bericht"
              value="support@staatsen.family"
              color="#10B981"
            />

            <ContactCard
              icon="people"
              title="Familie Beheerder"
              description="Voor accountproblemen of toegang"
              value="Neem contact op met een admin"
              color="#7C3AED"
            />
          </View>

          {/* FAQ Section */}
          <Text className="text-2xl font-bold text-gray-900 mb-6">
            Veelgestelde Vragen
          </Text>

          <View className="mb-8">
            <FAQItem
              question="Hoe maak ik een account aan?"
              answer="Je hebt een uitnodiging nodig van een familielid die al toegang heeft. 
              Bij het aanmaken wordt je gevraagd om het geheime familie-wachtwoord. 
              Vraag dit aan een familielid die de app al gebruikt."
            />

            <FAQItem
              question="Mijn kind heeft geen telefoon, hoe kan die meespelen?"
              answer="Via de 'Speel als' functie kun je als ouder/voogd je kind laten 
              meespelen op jouw telefoon. Ga naar het profiel van je kind en kies 
              'Speel als dit gezinslid'. Alle scores worden op hun naam bijgehouden."
            />

            <FAQItem
              question="Ik ben mijn wachtwoord vergeten"
              answer="Op het inlogscherm kun je kiezen voor 'Wachtwoord vergeten'. 
              Je ontvangt dan een e-mail met instructies om je wachtwoord te resetten."
            />

            <FAQItem
              question="Hoe verwijder ik mijn account?"
              answer="Ga naar Profiel → Instellingen → Account Verwijderen. Je kunt ook 
              contact opnemen met de familie-beheerder. Al je gegevens worden permanent 
              verwijderd."
            />

            <FAQItem
              question="Wie kan mijn foto's zien?"
              answer="Alleen andere familieleden met een account in de app kunnen je 
              gedeelde foto's zien. De foto's zijn niet publiek toegankelijk."
            />

            <FAQItem
              question="Hoe werkt de speurtocht?"
              answer="De speurtocht gebruikt je locatie om te bepalen of je bij een 
              checkpoint bent. Je moet locatietoegang geven om mee te kunnen doen. 
              Je locatie wordt alleen tijdens het spel gebruikt."
            />

            <FAQItem
              question="Kan ik meldingen uitschakelen?"
              answer="Ja, ga naar Profiel → Instellingen → Meldingen om je voorkeuren 
              aan te passen. Je kunt kiezen welke meldingen je wel of niet wilt ontvangen."
            />
          </View>

          {/* Still need help */}
          <View className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100">
            <View className="flex-row items-start gap-3">
              <Ionicons name="chatbubbles" size={24} color="#10B981" />
              <View className="flex-1">
                <Text className="text-lg font-semibold text-emerald-900">
                  Vraag niet beantwoord?
                </Text>
                <Text className="text-emerald-700 mt-1 leading-6">
                  Neem gerust contact op via e-mail of vraag het aan een 
                  familielid. We helpen je graag!
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>

      {/* Footer */}
      <View className="bg-gray-50 border-t border-gray-200 px-6 py-8">
        <View className="max-w-2xl mx-auto items-center">
          <Text className="text-sm text-gray-500 text-center">
            © {new Date().getFullYear()} Staatsen Familie App
          </Text>
        </View>
      </View>
    </View>
  );
}

